import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { extractGraphErrorMessage } from "@/lib/invokeError";
import {
  buildDefaultVariableValues,
  parseTemplateContent,
  substituteMetaVariables,
  type LeadTemplateContext,
} from "@/lib/whatsapp-template-runtime";
import { WhatsAppTemplatePreviewBubble } from "@/components/integrations/WhatsAppTemplatePreviewBubble";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, RefreshCw, Send, Sparkles } from "lucide-react";

interface TemplateRow {
  id: string;
  name: string;
  language: string;
  status: string;
  category: string | null;
  components: unknown;
}

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  conversationId: string;
  integrationId: string;
  leadId: string;
  leadName: string;
}

export function SendWhatsAppTemplateDialog({
  open,
  onOpenChange,
  conversationId,
  integrationId,
  leadId,
  leadName,
}: Props) {
  const { toast } = useToast();
  const [templates, setTemplates] = useState<TemplateRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [sending, setSending] = useState(false);
  const [selectedId, setSelectedId] = useState<string>("");
  const [bodyVars, setBodyVars] = useState<string[]>([]);
  const [headerVars, setHeaderVars] = useState<string[]>([]);
  const [leadPhone, setLeadPhone] = useState<string | null>(null);

  const selected = useMemo(
    () => templates.find((t) => t.id === selectedId) || null,
    [templates, selectedId],
  );

  const parsed = useMemo(
    () => parseTemplateContent((selected?.components as unknown[]) || []),
    [selected],
  );

  const leadContext: LeadTemplateContext = useMemo(
    () => ({ leadName, leadId, leadPhone }),
    [leadName, leadId, leadPhone],
  );

  const preview = useMemo(() => {
    const headerRaw = parsed.header?.format === "TEXT" ? parsed.header.text : undefined;
    const bodyRaw = parsed.body?.text || "";
    return {
      header: headerRaw ? substituteMetaVariables(headerRaw, headerVars) : undefined,
      body: bodyRaw ? substituteMetaVariables(bodyRaw, bodyVars) : "Selecione um template para ver a mensagem.",
      footer: parsed.footer?.text?.trim() || undefined,
      buttons: parsed.buttons,
    };
  }, [parsed, headerVars, bodyVars]);

  const loadLeadContext = async () => {
    const { data } = await supabase.from("leads").select("phone").eq("id", leadId).maybeSingle();
    setLeadPhone(data?.phone ?? null);
  };

  const loadTemplates = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("whatsapp_cloud_templates")
      .select("id, name, language, status, category, components")
      .eq("integration_id", integrationId)
      .eq("status", "APPROVED")
      .order("name");
    setLoading(false);
    if (error) {
      toast({ title: "Erro ao carregar templates", description: error.message, variant: "destructive" });
      return;
    }
    setTemplates((data as TemplateRow[]) || []);
  };

  useEffect(() => {
    if (!open) return;
    void loadLeadContext();
    void loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, integrationId, leadId]);

  useEffect(() => {
    if (!selectedId || !selected) {
      setBodyVars([]);
      setHeaderVars([]);
      return;
    }
    const ctx: LeadTemplateContext = { leadName, leadId, leadPhone };
    setHeaderVars(buildDefaultVariableValues(parsed.headerLabels, ctx));
    setBodyVars(buildDefaultVariableValues(parsed.bodyLabels, ctx));
  }, [
    selectedId,
    leadName,
    leadId,
    leadPhone,
    parsed.headerLabels.join("\0"),
    parsed.bodyLabels.join("\0"),
  ]);

  const handleSync = async () => {
    setSyncing(true);
    const { data, error } = await supabase.functions.invoke("whatsapp-cloud-templates-sync", {
      body: { integration_id: integrationId },
    });
    setSyncing(false);
    if (error || (data as { error?: string })?.error) {
      toast({
        title: "Falha ao sincronizar",
        description: error?.message || (data as { error?: string })?.error || "Erro desconhecido",
        variant: "destructive",
      });
      return;
    }
    toast({
      title: "Templates sincronizados",
      description: `${(data as { synced?: number })?.synced ?? 0} templates atualizados.`,
    });
    await loadTemplates();
  };

  const applyLeadDefaults = () => {
    setHeaderVars(buildDefaultVariableValues(parsed.headerLabels, leadContext));
    setBodyVars(buildDefaultVariableValues(parsed.bodyLabels, leadContext));
    toast({
      title: "Dados do contato aplicados",
      description: "Os campos foram preenchidos com as informações deste lead.",
    });
  };

  const handleSend = async () => {
    if (!selected) return;
    setSending(true);
    const { data, error } = await supabase.functions.invoke("whatsapp-cloud-send-template", {
      body: {
        conversation_id: conversationId,
        template_id: selected.id,
        variables: {
          body: bodyVars,
          header: headerVars,
        },
      },
    });
    setSending(false);

    const payload = data as { error?: string; graph_error?: unknown; ok?: boolean } | null;
    if (error || payload?.error) {
      const graphMsg = extractGraphErrorMessage(payload?.graph_error);
      toast({
        title: "Falha ao enviar template",
        description: graphMsg || payload?.error || error?.message || "Erro desconhecido",
        variant: "destructive",
      });
      return;
    }

    toast({ title: "Mensagem enviada", description: "O template foi enviado para o contato." });
    onOpenChange(false);
  };

  const hasVariables = parsed.headerVarCount > 0 || parsed.bodyVarCount > 0;
  const canSend =
    selected &&
    !sending &&
    bodyVars.every((v) => v.trim()) &&
    headerVars.every((v) => v.trim());

  const templatesUrl = `/integrations/whatsapp-cloud/templates?integration=${integrationId}`;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Enviar mensagem modelo</DialogTitle>
          <DialogDescription>
            Para falar com <span className="font-medium text-foreground">{leadName}</span> fora da janela de
            24 horas, escolha um template aprovado e confira como a mensagem vai aparecer.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-6 md:grid-cols-2">
          <div className="space-y-4">
            <div className="flex items-end gap-2">
              <div className="flex-1 space-y-1">
                <Label>Template aprovado</Label>
                <Select
                  value={selectedId}
                  onValueChange={setSelectedId}
                  disabled={loading || templates.length === 0}
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={
                        loading
                          ? "Carregando templates..."
                          : templates.length === 0
                            ? "Nenhum template disponível"
                            : "Escolha um template"
                      }
                    />
                  </SelectTrigger>
                  <SelectContent>
                    {templates.map((t) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.name.replace(/_/g, " ")}
                        <span className="text-muted-foreground"> · {t.language}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={handleSync}
                disabled={syncing}
                title="Buscar templates na Meta"
              >
                {syncing ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>

            {!loading && templates.length === 0 && (
              <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground space-y-2">
                <p>Não há templates aprovados para este número.</p>
                <p>
                  Crie um template em{" "}
                  <Link to={templatesUrl} className="text-primary underline-offset-4 hover:underline">
                    Integrações → Templates WhatsApp
                  </Link>{" "}
                  ou sincronize com a Meta.
                </p>
              </div>
            )}

            {selected && hasVariables && (
              <div className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <Label className="text-sm">Personalizar para este contato</Label>
                  <Button type="button" variant="ghost" size="sm" className="h-8" onClick={applyLeadDefaults}>
                    <Sparkles className="mr-1.5 h-3.5 w-3.5" />
                    Usar dados do lead
                  </Button>
                </div>

                {parsed.headerVarCount > 0 && (
                  <div className="space-y-2 rounded-lg border p-3">
                    <p className="text-xs font-medium text-muted-foreground">Cabeçalho</p>
                    {headerVars.map((v, i) => (
                      <div key={`h-${i}`} className="space-y-1">
                        <Label className="text-xs">{parsed.headerLabels[i]}</Label>
                        <Input
                          value={v}
                          placeholder={`Ex.: ${leadName}`}
                          onChange={(e) =>
                            setHeaderVars((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}

                {parsed.bodyVarCount > 0 && (
                  <div className="space-y-2 rounded-lg border p-3">
                    <p className="text-xs font-medium text-muted-foreground">Mensagem</p>
                    {bodyVars.map((v, i) => (
                      <div key={`b-${i}`} className="space-y-1">
                        <Label className="text-xs">{parsed.bodyLabels[i]}</Label>
                        <Input
                          value={v}
                          placeholder={
                            i === 0
                              ? `Ex.: ${leadName}`
                              : i === 1 && leadPhone
                                ? `Ex.: ${leadPhone}`
                                : "Preencha este campo"
                          }
                          onChange={(e) =>
                            setBodyVars((prev) => prev.map((x, idx) => (idx === i ? e.target.value : x)))
                          }
                        />
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {selected && !hasVariables && (
              <p className="text-sm text-muted-foreground rounded-lg border bg-muted/30 p-3">
                Este template não tem campos personalizáveis — a mensagem será enviada como está.
              </p>
            )}
          </div>

          <div className="space-y-2 md:sticky md:top-0 md:self-start">
            <Label className="text-xs text-muted-foreground">Como o contato vai ver</Label>
            <div className="rounded-xl bg-[#0b141a] p-4 min-h-[220px]">
              <WhatsAppTemplatePreviewBubble
                headerText={preview.header}
                bodyText={preview.body}
                footerText={preview.footer}
                buttons={preview.buttons}
              />
            </div>
            {selected && hasVariables && (
              <p className="text-xs text-muted-foreground">
                Os campos acima substituem as partes destacadas na mensagem — cada envio pode ter valores
                diferentes.
              </p>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={handleSend} disabled={!canSend}>
            {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
            Enviar mensagem
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default SendWhatsAppTemplateDialog;
